/**
 * Instruction Adapter
 * Transforms instruction data between API and application layer
 */

import { BaseAdapter } from './base.adapter';
import type {
  Instruction,
  CreateInstructionDto,
  UpdateInstructionDto,
  ToggleStatusDto,
  InstructionResponseDto,
} from '@/types';

export class InstructionAdapter extends BaseAdapter {
  /**
   * Adapts instruction from API response
   * Handles both populated objects and string IDs for relationships
   */
  static fromApi(dto: InstructionResponseDto): Instruction {
    // Helper to extract IDs from potentially populated fields
    const extractIds = (items: (string | { id: string })[]): string[] => {
      return items.map((item) => (typeof item === 'string' ? item : item.id));
    };

    return {
      id: dto.id,
      name: dto.name,
      description: dto.description,
      type: dto.type,
      status: dto.status,
      createdBy: dto.createdBy,
      tags: dto.tags || [],
      knowledgeBases: extractIds(dto.knowledgeBases || []),
      actionData: dto.actionData,
      createdAt: dto.createdAt,
      updatedAt: dto.updatedAt
    };
  }

  /**
   * Adapts instructions array from API response
   */
  static fromApiList(dtos: InstructionResponseDto[]): Instruction[] {
    return dtos.map((dto) => this.fromApi(dto));
  }

  /**
   * Adapts create instruction request
   */
  static toCreateRequest(data: CreateInstructionDto): CreateInstructionDto {
    return this.sanitizePayload({
      name: data.name.trim(),
      description: data.description.trim(),
      type: data.type,
      status: data.status,
      knowledgeBases: data.knowledgeBases,
      actionData: data.actionData
    });
  }

  /**
   * Adapts update instruction request
   */
  static toUpdateRequest(data: UpdateInstructionDto): UpdateInstructionDto {
    const payload: UpdateInstructionDto = {};

    if (data.name !== undefined) payload.name = data.name.trim();
    if (data.description !== undefined) payload.description = data.description.trim();
    if (data.type !== undefined) payload.type = data.type;
    if (data.status !== undefined) payload.status = data.status;
    if (data.actionData !== undefined) payload.actionData = data.actionData;
    if (data.knowledgeBases !== undefined) payload.knowledgeBases = data.knowledgeBases;

    return this.sanitizePayload(payload);
  }

  /**
   * Adapts toggle status request
   */
  static toToggleStatusRequest(status: boolean): ToggleStatusDto {
    return { status };
  }
}
